import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Globe, Sun, Moon, Check, ArrowUpRight } from "lucide-react";
import { scrollToForm } from "./CTAButton";
import { BRAND } from "../../lib/landingData";
import { LogoMark } from "./LogoMark";
import { useLang } from "../../i18n/LanguageContext";
import { useJourney } from "../../context/JourneyContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../ui/dropdown-menu";

export const Header = () => {
  const { t, lang, setLang, langs } = useLang();
  const { journey, resetJourney } = useJourney();
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const dark = localStorage.getItem("fba_theme") === "dark";
    setIsDark(dark);
    document.documentElement.classList.toggle("dark", dark);
  }, []);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem("fba_theme", next ? "dark" : "light");
    document.documentElement.classList.toggle("dark", next);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-4 flex items-center justify-between
                      bg-[var(--page)]/80 backdrop-blur-md border-b border-[var(--hairline)]">
        <button
          data-testid="brand-logo"
          onClick={() => (window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }))}
          className="flex items-center gap-2.5 text-left"
        >
          <LogoMark className="h-9 w-auto shrink-0" />
          <span className="flex flex-col leading-none">
            <span className="font-display text-[var(--ink)] text-base sm:text-xl leading-none tracking-tight" data-testid="brand-name">
              {BRAND.name}
            </span>
            <span className="hidden sm:block mt-1 text-[10px] uppercase tracking-[0.18em] text-[var(--muted-ink)]" data-testid="brand-tagline">
              {t.brand.tagline}
            </span>
          </span>
        </button>

        <div className="flex items-center gap-1.5 sm:gap-3">
          {journey && (
            <button
              data-testid="change-journey"
              onClick={resetJourney}
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[var(--hairline)]
                         text-[var(--ink)] text-[11px] font-semibold uppercase tracking-wide transition-colors hover:bg-[var(--ink)]/5"
            >
              {journey === "local" ? t.headerPill.local : t.headerPill.foreign}
              <span className="opacity-50">· {t.headerPill.change}</span>
            </button>
          )}
          <a
            href={BRAND.taxSite}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-tax-services"
            className="hidden md:inline-flex items-center gap-1 px-3 py-2 rounded-[3px] border border-[var(--hairline)]
                       text-[var(--ink)] text-xs font-semibold transition-colors hover:bg-[var(--ink)]/5"
          >
            {t.brand.navTax} <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          {/* Language switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="language-switcher"
                aria-label="Change language"
                className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-[3px] border border-[var(--hairline)]
                           text-[var(--ink)] text-xs font-semibold transition-colors hover:bg-[var(--ink)]/5"
              >
                <Globe className="h-4 w-4" strokeWidth={1.8} />
                <span className="hidden sm:inline">{langs.find((l) => l.code === lang)?.label}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto">
              {langs.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className="flex items-center justify-between gap-6 cursor-pointer"
                >
                  <span>{l.label}</span>
                  {lang === l.code && <Check className="h-4 w-4 text-terra" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme toggle */}
          <button
            data-testid="theme-toggle"
            aria-label="Toggle dark mode"
            onClick={toggleTheme}
            className="inline-flex items-center justify-center h-9 w-9 rounded-[3px] border border-[var(--hairline)]
                       text-[var(--ink)] transition-colors hover:bg-[var(--ink)]/5"
          >
            {isDark ? <Sun className="h-4 w-4" strokeWidth={1.8} /> : <Moon className="h-4 w-4" strokeWidth={1.8} />}
          </button>

          <button
            data-testid="header-cta"
            onClick={scrollToForm}
            className="hidden sm:inline-flex items-center gap-2 px-6 py-2.5 rounded-[3px] bg-terra text-cream
                       text-xs font-semibold uppercase tracking-wide transition-colors hover:bg-terra-dark"
          >
            {t.nav.getGuide} <span className="cta-arrow">→</span>
          </button>
        </div>
      </div>
    </motion.header>
  );
};
